import { SetStateAction, useCallback, useMemo, useSyncExternalStore } from 'react';

import { reconcileColumnOrder } from '~components/DataTable/dataTableSavedState';
import {
  getSharedLayoutState,
  resetSharedLayout,
  subscribeToSharedLayout,
  updateSharedLayout,
} from '~components/DataTable/dataTableSharedLayout';

function applyUpdate<T>(update: SetStateAction<T>, previous: T) {
  return update instanceof Function ? update(previous) : update;
}

export default function useDataTableSavedState(
  tableId: string,
  defaultOrder: string[],
  defaultVisibility: Record<string, boolean>,
) {
  const subscribe = useCallback(
    (listener: () => void) => subscribeToSharedLayout(tableId, listener),
    [tableId],
  );
  const savedState = useSyncExternalStore(subscribe, () => getSharedLayoutState(tableId));

  const columnOrder = useMemo(
    () => reconcileColumnOrder(savedState.columnOrder, defaultOrder),
    [savedState.columnOrder, defaultOrder],
  );

  const columnVisibility = useMemo(
    () => ({ ...defaultVisibility, ...savedState.columnVisibility }),
    [defaultVisibility, savedState.columnVisibility],
  );

  const setColumnOrder = useCallback(
    (updater: SetStateAction<string[]>) => {
      updateSharedLayout(tableId, (previous) => ({
        ...previous,
        columnOrder: applyUpdate(
          updater,
          reconcileColumnOrder(previous.columnOrder, defaultOrder),
        ),
      }));
    },
    [defaultOrder, tableId],
  );

  const setColumnVisibility = useCallback(
    (updater: SetStateAction<Record<string, boolean>>) => {
      updateSharedLayout(tableId, (previous) => {
        const visibility = applyUpdate(updater, {
          ...defaultVisibility,
          ...previous.columnVisibility,
        });
        const userChoices = Object.entries(visibility).filter(
          ([field, isVisible]) => isVisible !== (defaultVisibility[field] ?? true),
        );

        return { ...previous, columnVisibility: Object.fromEntries(userChoices) };
      });
    },
    [defaultVisibility, tableId],
  );

  const setColumnWidth = useCallback(
    (field: string, width: number | null) => {
      updateSharedLayout(tableId, (previous) => {
        const columnWidths = { ...previous.columnWidths };

        if (width === null) {
          delete columnWidths[field];
        } else {
          columnWidths[field] = width;
        }

        return { ...previous, columnWidths };
      });
    },
    [tableId],
  );

  const reset = useCallback(() => resetSharedLayout(tableId), [tableId]);

  return {
    columnOrder,
    columnVisibility,
    columnWidths: savedState.columnWidths,
    reset,
    setColumnOrder,
    setColumnVisibility,
    setColumnWidth,
  };
}
